import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/Badge'
import type { RegionDashboard, StructureDetail } from '@/lib/dashboardApi'

// ── Types ──────────────────────────────────────────────────

interface RegionPanelProps {
  regionDashboard: RegionDashboard | null
  loading: boolean
  onClose: () => void
}

// ── Freshness badge config ──────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info'

const FRESHNESS_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  CONFIRMED: { label: 'Confirmé',  variant: 'success' },
  STALE:     { label: 'Périmé',    variant: 'warning' },
  UNKNOWN:   { label: 'Inconnu',   variant: 'default' },
}

function getFreshnessConfig(freshness: string): { label: string; variant: BadgeVariant } {
  return FRESHNESS_CONFIG[freshness] ?? { label: freshness, variant: 'default' }
}

// ── Bed occupancy color ─────────────────────────────────────

function bedBarColor(rate: number): string {
  if (rate > 0.8) return 'bg-error-500'
  if (rate > 0.6) return 'bg-warning-400'
  return 'bg-success-500'
}

// ── StructureRow (inline sub-component) ────────────────────

interface StructureRowProps {
  structure: StructureDetail
}

function StructureRow({ structure }: StructureRowProps) {
  const { name, identifierCode, freshness, beds, orientationsToday, stocks } = structure

  const occupancyRate = beds.total > 0 ? beds.occupied / beds.total : 0
  const occupancyPct  = Math.round(occupancyRate * 100)
  const freshnessConf = getFreshnessConfig(freshness)
  const barColor      = bedBarColor(occupancyRate)
  const hasStockAlerts = stocks.criticalBelow.length > 0

  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-sm space-y-2">
      {/* Header: name + code + freshness */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 truncate">{name}</p>
          <p className="text-xs font-mono text-neutral-400">{identifierCode}</p>
        </div>
        <Badge variant={freshnessConf.variant} size="sm" className="shrink-0">
          {freshnessConf.label}
        </Badge>
      </div>

      {/* Bed progress bar */}
      <div className="space-y-0.5">
        <div className="flex justify-between text-xs text-neutral-500">
          <span>Lits occupés</span>
          <span className="font-medium text-neutral-700">
            {beds.occupied}/{beds.total}
            <span className="ml-1 text-neutral-400">({occupancyPct}%)</span>
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', barColor)}
            style={{ width: `${Math.min(occupancyPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Footer: available beds / orientations / stock alerts */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-xs text-success-700 font-medium">
          {beds.available} lits disponibles
        </span>

        {orientationsToday > 0 && (
          <span className="text-xs text-neutral-500">
            {orientationsToday} orientation{orientationsToday > 1 ? 's' : ''} aujourd'hui
          </span>
        )}

        {hasStockAlerts && (
          <span className="text-xs font-medium text-error-600">
            ⚠ {stocks.criticalBelow.length} alerte{stocks.criticalBelow.length > 1 ? 's' : ''} stock
          </span>
        )}
      </div>
    </div>
  )
}

// ── RegionPanel ─────────────────────────────────────────────

export function RegionPanel({ regionDashboard, loading, onClose }: RegionPanelProps) {
  return (
    <div className="flex flex-col h-full rounded-xl border border-neutral-200 bg-neutral-50 shadow-sm overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white">
        <h2 className="text-sm font-semibold text-neutral-800 truncate">
          {regionDashboard ? regionDashboard.name : 'Détails de la région'}
        </h2>
        <button
          onClick={onClose}
          aria-label="Fermer le panneau"
          className="ml-2 shrink-0 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors duration-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>

      {/* Panel body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <svg
              className="h-7 w-7 animate-spin text-primary-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-label="Chargement"
            >
              <circle
                className="opacity-25"
                cx="12" cy="12" r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        )}

        {/* Empty / placeholder state */}
        {!loading && !regionDashboard && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-10 w-10 text-neutral-300 mb-3"
              aria-hidden="true"
            >
              <path d="M12 21C12 21 4 13.5 4 8.5a8 8 0 1 1 16 0c0 5-8 12.5-8 12.5Z" />
              <circle cx="12" cy="8.5" r="2.5" />
            </svg>
            <p className="text-sm text-neutral-400">
              Cliquez sur une région pour voir les détails
            </p>
          </div>
        )}

        {/* Structures list */}
        {!loading && regionDashboard && regionDashboard.structures.length === 0 && (
          <p className="py-10 text-center text-sm text-neutral-400">
            Aucune structure dans cette région.
          </p>
        )}

        {!loading && regionDashboard && regionDashboard.structures.map((structure) => (
          <StructureRow key={structure.id} structure={structure} />
        ))}
      </div>
    </div>
  )
}
